import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FiTrendingDown, FiTrendingUp, FiStar, FiFilter, FiUsers, FiTarget, FiClock, FiMessageCircle, FiImage, FiHeart, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { getTransformations, getReviews } from '../utils/api';
import { getTransformationImageUrl, getReviewImageUrl, debugImageData } from '../utils/imageUtils';

const TransformationCard = ({ transformation, index }) => {
  const [imageType, setImageType] = useState('after');
  const [beforeError, setBeforeError] = useState(false);
  const [afterError, setAfterError] = useState(false);

  const renderImage = () => {
    if (imageType === 'before') {
      if (beforeError || !transformation.before_image) {
        return (
          <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center">
            <div className="text-center text-primary-800">
              <div className="text-lg font-medium text-visible-orange">Before</div>
            </div>
          </div>
        );
      }
      const imgSrc = getTransformationImageUrl(transformation.before_image);
      return (
        <motion.div
          key="before"
          className="absolute inset-0 w-full h-full flex items-center justify-center bg-neutral-900"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Blurred Background Cover */}
          <img
            src={imgSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-md opacity-40 scale-105 select-none pointer-events-none"
          />
          {/* Main Uncropped Image */}
          <img
            src={imgSrc}
            alt={`${transformation.client_name} - Before`}
            className="relative max-w-full max-h-full w-full h-full object-contain z-10"
            onError={() => setBeforeError(true)}
          />
        </motion.div>
      );
    } else {
      if (afterError || !transformation.after_image) {
        return (
          <div className="w-full h-full bg-gradient-to-br from-secondary-100 to-secondary-200 flex items-center justify-center">
            <div className="text-center text-secondary-800">
              <div className="text-lg font-medium text-orange-600">After</div>
            </div>
          </div>
        );
      }
      const imgSrc = getTransformationImageUrl(transformation.after_image);
      return (
        <motion.div
          key="after"
          className="absolute inset-0 w-full h-full flex items-center justify-center bg-neutral-900"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          {/* Blurred Background Cover */}
          <img
            src={imgSrc}
            alt=""
            className="absolute inset-0 w-full h-full object-cover filter blur-md opacity-40 scale-105 select-none pointer-events-none"
          />
          {/* Main Uncropped Image */}
          <img
            src={imgSrc}
            alt={`${transformation.client_name} - After`}
            className="relative max-w-full max-h-full w-full h-full object-contain z-10"
            onError={() => setAfterError(true)}
          />
        </motion.div>
      );
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, delay: index * 0.1 }}
      className="card card-hover overflow-hidden h-full flex flex-col"
      onMouseEnter={() => setImageType('before')}
      onMouseLeave={() => setImageType('after')}
    >
      {/* Unified Image Section */}
      <div className="h-72 relative overflow-hidden bg-neutral-100 flex-shrink-0 select-none">
        <AnimatePresence mode="wait">
          {renderImage()}
        </AnimatePresence>

        {/* Dynamic Before/After Active Indicator */}
        <div className={`absolute top-2 left-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-sm transition-colors duration-300 ${
          imageType === 'before' 
            ? 'bg-red-500' 
            : 'bg-green-500'
        }`}>
          {imageType === 'before' ? 'Before' : 'After'}
        </div>

        {/* Before / After Floating Segmented Pill */}
        {transformation.before_image && transformation.after_image && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center bg-black/60 backdrop-blur-md p-1 rounded-full text-[11px] font-semibold text-white border border-white/10 z-10 shadow-lg">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImageType('before');
              }}
              className={`px-3 py-1 rounded-full transition-all duration-300 ${
                imageType === 'before' 
                  ? 'bg-orange-500 text-white shadow-sm' 
                  : 'text-neutral-300 hover:text-white'
              }`}
            >
              Before
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setImageType('after');
              }}
              className={`px-3 py-1 rounded-full transition-all duration-300 ${
                imageType === 'after' 
                  ? 'bg-orange-500 text-white shadow-sm' 
                  : 'text-neutral-300 hover:text-white'
              }`}
            >
              After
            </button>
          </div>
        )}
        
        {/* Goal Type Badge */}
        <div className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${
          transformation.goal_type === 'weight_loss' 
            ? 'bg-blue-100 text-blue-800' 
            : 'bg-secondary-100 text-secondary-800'
        }`}>
          {transformation.goal_type === 'weight_loss' ? 'Weight Loss' : 'Weight Gain'}
        </div>
      </div>

      {/* Compact Content Section */}
      <div className="p-4 flex-grow flex flex-col justify-between">
        <div>
          <h3 className="text-sm font-semibold text-secondary-900 mb-1">
            {transformation.client_name}
          </h3>
          
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div className="text-center">
              <div className="text-base font-bold text-primary-600">
                {transformation.weight_change}kg
              </div>
              <div className="text-xs text-secondary-600">
                {transformation.goal_type === 'weight_loss' ? 'Lost' : 'Gained'}
              </div>
            </div>
            <div className="text-center">
              <div className="text-base font-bold text-secondary-900">
                {transformation.duration_months}
              </div>
              <div className="text-xs text-secondary-600">Months</div>
            </div>
          </div>

          <div className="mb-1">
            <p className="text-xs text-secondary-600">
              <strong>Before:</strong> {transformation.before_weight}kg → 
              <strong> After:</strong> {transformation.after_weight}kg
            </p>
          </div>

          {transformation.story && (
            <div className="mb-1">
              <p className="text-xs text-secondary-600 line-clamp-1">
                {transformation.story}
              </p>
            </div>
          )}

          {transformation.testimonial && (
            <div className="bg-gray-50 rounded p-1.5 mb-1">
              <p className="text-xs text-secondary-700 italic line-clamp-2">
                "{transformation.testimonial}"
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
          <div className="flex items-center text-amber-500">
            {[...Array(5)].map((_, i) => (
              <FiStar key={i} className="w-2.5 h-2.5 fill-current" />
            ))}
          </div>
          <span className="text-xs text-secondary-500">
            {new Date(transformation.created_at).toLocaleDateString()}
          </span>
        </div>
      </div>
    </motion.div>
  );
};

const Transformations = () => {
  const [transformations, setTransformations] = useState([]);
  const [filteredTransformations, setFilteredTransformations] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('reviews');
  const [currentSlide, setCurrentSlide] = useState(0);
  const [autoPlay, setAutoPlay] = useState(true);
  const [isContinuousAnimating, setIsContinuousAnimating] = useState(true);

  const [heroRef, heroInView] = useInView({ threshold: 0.1, triggerOnce: true });
  const [statsRef, statsInView] = useInView({ threshold: 0.1, triggerOnce: true });

  useSEO({
    title: 'Client Transformations & Testimonials',
    description: 'Read the success stories and before/after transformation journeys of clients at Dfitclub. Certified clinical nutrition plans showing real results for weight loss and PCOS.',
    keywords: 'weight loss success stories, client transformations, dietitian reviews, Dfitclub reviews, before after weight loss',
  });

  useEffect(() => {
    const fetchTransformations = async () => {
      try {
        const data = await getTransformations();
        console.log('🔍 Transformations data received:', data);
        
        // Debug each transformation's image data
        if (data.transformations && data.transformations.length > 0) {
          data.transformations.forEach(transformation => {
            debugImageData(transformation, 'transformation');
          });
        }
        
        setTransformations(data.transformations);
        setFilteredTransformations(data.transformations);
      } catch (error) {
        console.error('Failed to fetch transformations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTransformations();
  }, []);

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        setReviewsLoading(true);
        console.log('🔄 Fetching reviews from API...');
        console.log('🌐 API Base URL:', process.env.REACT_APP_API_URL || '/api (using proxy)');
        console.log('🔑 API Key configured:', !!process.env.REACT_APP_API_KEY);
        
        const data = await getReviews();
        console.log('🔍 Reviews data received:', data);
        console.log('📊 Reviews count:', data?.reviews?.length || 0);
        
        if (!data || !data.reviews) {
          console.warn('⚠️ No reviews data in response:', data);
          setReviews([]);
          return;
        }
        
        // Map API response to match frontend format
        const formattedReviews = (data.reviews || []).map(review => {
          console.log('📝 Processing review:', review);
          return {
            id: review.id,
            type: review.type,
            content: review.content,
            author: review.author,
            date: review.date || review.created_at,
            rating: review.rating || 5,
            image: review.image || null
          };
        });
        
        console.log('✅ Formatted reviews:', formattedReviews);
        console.log('✅ Setting reviews state with', formattedReviews.length, 'reviews');
        setReviews(formattedReviews);
      } catch (error) {
        console.error('❌ Failed to fetch reviews:', error);
        console.error('❌ Error response:', error.response);
        console.error('❌ Error message:', error.message);
        console.error('❌ Error stack:', error.stack);
        if (error.response) {
          console.error('❌ Response status:', error.response.status);
          console.error('❌ Response data:', error.response.data);
        }
        // Fallback to empty array if API fails
        setReviews([]);
      } finally {
        setReviewsLoading(false);
      }
    };

    fetchReviews();
  }, []);

  // Continuous animation for Journey carousel
  useEffect(() => {
    if (activeTab === 'journey' && filteredTransformations.length > 0 && autoPlay && filteredTransformations.length > getCardsPerView() && isContinuousAnimating) {
      let animationId;
      let startTime = Date.now();
      const animationDuration = 20000; // 20 seconds for one complete cycle
      const totalSlides = filteredTransformations.length * 2;
      const maxSlides = totalSlides - getCardsPerView();
      
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = (elapsed % animationDuration) / animationDuration;
        const currentSlide = Math.floor(progress * maxSlides);
        
        setCurrentSlide(currentSlide);
        animationId = requestAnimationFrame(animate);
      };
      
      animationId = requestAnimationFrame(animate);
      
      return () => {
        if (animationId) {
          cancelAnimationFrame(animationId);
        }
      };
    }
  }, [filteredTransformations.length, autoPlay, isContinuousAnimating]);



  const getCardsPerView = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth >= 1440) return 3; // Large desktop
      if (window.innerWidth >= 1024) return 3; // Desktop
      if (window.innerWidth >= 768) return 2;  // Tablet
      return 1; // Mobile
    }
    return 3; // Default
  };

  const handleFilter = (filter) => {
    setActiveFilter(filter);
    setCurrentSlide(0); // Reset to first slide when filter changes
    if (filter === 'all') {
      setFilteredTransformations(transformations);
    } else {
      setFilteredTransformations(transformations.filter(t => t.goal_type === filter));
    }
  };

  const toggleContinuousAnimation = () => {
    setIsContinuousAnimating(!isContinuousAnimating);
  };

  const pauseAnimation = () => {
    setIsContinuousAnimating(false);
  };

  const resumeAnimation = () => {
    setIsContinuousAnimating(true);
  };

  const filters = [
    { key: 'all', label: 'All Stories', icon: FiUsers },
    { key: 'weight_loss', label: 'Weight Loss', icon: FiTrendingDown },
    { key: 'weight_gain', label: 'Weight Gain', icon: FiTrendingUp }
  ];

  const stats = [
    { icon: FiUsers, value: '100+', label: 'Success Stories' },
    { icon: FiTarget, value: '95%', label: 'Success Rate' },
    { icon: FiStar, value: '4.8', label: 'Average Rating' },
    { icon: FiClock, value: '4', label: 'Months Average' }
  ];


  const renderJourneyTab = () => (
    <div className="space-y-8">
      {/* Filter Section */}
      <section className="py-8 bg-gray-50">
        <div className="container-max">
          <div className="flex flex-wrap justify-center gap-4">
            {filters.map((filter) => (
              <button
                key={filter.key}
                onClick={() => handleFilter(filter.key)}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  activeFilter === filter.key
                    ? 'bg-primary-600 text-white'
                                          : 'bg-green-500 text-white hover:bg-green-600'
                  }`}
              >
                <filter.icon className="w-5 h-5" />
                <span>{filter.label}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Multiple Cards Carousel */}
      <section className="section-padding bg-gray-50">
        <div className="container-max">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="card animate-pulse">
                  <div className="h-96 bg-gray-200 rounded-t-xl"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                    <div className="h-16 bg-gray-200 rounded"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredTransformations.length === 0 ? (
            <div className="text-center py-8">
              <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <FiUsers className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-xl font-semibold text-secondary-900 mb-2">No transformations found</h3>
              <p className="text-secondary-600">Try changing the filter or check back later for more stories.</p>
            </div>
          ) : filteredTransformations.length <= getCardsPerView() ? (
            /* Simple Grid for small number of items */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-center">
              {filteredTransformations.map((transformation, index) => (
                <div key={transformation.id} className="w-full max-w-sm mx-auto">
                  <TransformationCard transformation={transformation} index={index} />
                </div>
              ))}
            </div>
          ) : (
            <div className="relative">
              {/* Continuous Carousel Container */}
              <div className="continuous-carousel">
                <div 
                  className={`carousel-track ${isContinuousAnimating ? 'continuous-scroll' : ''}`}
                  style={{
                    width: `${((filteredTransformations.length * 2) / getCardsPerView()) * 100}%`
                  }}
                >
                  {/* Duplicate transformations for continuous effect */}
                  {[...filteredTransformations, ...filteredTransformations].map((transformation, index) => (
                    <div
                      key={`${transformation.id}-${index}`}
                      className="carousel-item px-4"
                      style={{ width: `${100 / (filteredTransformations.length * 2)}%` }}
                    >
                      <TransformationCard transformation={transformation} index={index} />
                    </div>
                  ))}
                </div>
              </div>

              {/* Animation Control */}
              {filteredTransformations.length > getCardsPerView() && (
                <div className="absolute top-4 right-4 z-10">
                  <button
                    onClick={toggleContinuousAnimation}
                    className="bg-green-500 text-white px-4 py-2 rounded-full shadow-lg transition-all duration-200 text-sm font-medium hover:bg-green-600"
                  >
                    {isContinuousAnimating ? '⏸️ Pause' : '▶️ Play'}
                  </button>
                </div>
              )}



              {/* Auto-play toggle */}
              <div className="flex justify-center mt-4">
                <button
                  onClick={() => {
                    setAutoPlay(!autoPlay);
                    if (autoPlay) {
                      setIsContinuousAnimating(false);
                    } else {
                      setIsContinuousAnimating(true);
                    }
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    autoPlay 
                      ? 'bg-primary-600 text-white' 
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  {autoPlay ? '⏸️ Pause Auto-play' : '▶️ Start Auto-play'}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );

  const renderReviewsTab = () => (
    <div className="section-padding bg-gray-50">
      <div className="container-max">
        {reviewsLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="card animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-xl"></div>
                <div className="p-6">
                  <div className="h-4 bg-gray-200 rounded mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-2/3 mb-4"></div>
                  <div className="h-16 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <FiStar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No reviews yet</h3>
            <p className="text-gray-600">Check back later for client reviews and testimonials.</p>
          </div>
        ) : reviews.length <= getCardsPerView() ? (
          /* Simple Grid for small number of items */
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto justify-center">
            {reviews.map((review, index) => (
              <div key={review.id} className="w-full max-w-sm mx-auto">
                <motion.div
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8, delay: index * 0.1 }}
                  className="card card-hover overflow-hidden flex flex-col h-full"
                >


                  {/* Image First (if present) */}
                  {review.image && (
                    <div className="w-full aspect-video max-h-40 overflow-hidden relative bg-gray-100 flex-shrink-0">
                      <img 
                        src={getReviewImageUrl(review.image)} 
                        alt="Review" 
                        className="w-full h-full object-cover object-center"
                        style={{ minHeight: '0', maxHeight: '160px' }}
                        onError={(e) => {
                          console.error('❌ Failed to load review image:', review.image);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                  )}

                  {/* Content */}
                  <div className="p-5 flex-1 flex flex-col">
                    {/* Rating */}
                    <div className="flex items-center text-amber-500 mb-3">
                      {[...Array(review.rating)].map((_, i) => (
                        <FiStar key={i} className="w-5 h-5 fill-current" />
                      ))}
                    </div>

                    {/* Review Content */}
                    <p className="text-secondary-700 mb-3 text-sm leading-relaxed flex-1">
                      "{review.content}"
                    </p>

                    {/* Author */}
                    <div className="mt-auto pt-4 border-t border-gray-200">
                      <h4 className="font-semibold text-secondary-900">{review.author}</h4>
                    </div>
                  </div>
                </motion.div>
              </div>
            ))}
          </div>
        ) : (
          <div className="relative">
            {/* Continuous Carousel */}
            <div className="continuous-carousel">
              <div
                className="carousel-track continuous-scroll"
                style={{
                  width: `${((reviews.length * 2) / getCardsPerView()) * 100}%`
                }}
              >
                {/* Duplicate reviews for continuous effect */}
                {[...reviews, ...reviews].map((review, index) => (
                  <div
                    key={`${review.id}-${index}`}
                    className="carousel-item px-4"
                    style={{ width: `${100 / (reviews.length * 2)}%` }}
                  >
                    <motion.div
                      initial={{ opacity: 0, y: 30 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.8, delay: index * 0.1 }}
                      className="card card-hover overflow-hidden flex flex-col h-full"
                    >


                      {/* Image First (if present) */}
                      {review.image && (
                        <div className="w-full aspect-video max-h-40 overflow-hidden relative bg-gray-100 flex-shrink-0">
                          <img 
                            src={getReviewImageUrl(review.image)} 
                            alt="Review" 
                            className="w-full h-full object-cover object-center"
                            style={{ minHeight: '0', maxHeight: '160px' }}
                            onError={(e) => {
                              console.error('❌ Failed to load review image:', review.image);
                              e.target.style.display = 'none';
                            }}
                          />
                        </div>
                      )}

                      {/* Content */}
                      <div className="p-5 flex-1 flex flex-col">
                        {/* Rating */}
                        <div className="flex items-center text-amber-500 mb-3">
                          {[...Array(review.rating)].map((_, i) => (
                            <FiStar key={i} className="w-5 h-5 fill-current" />
                          ))}
                        </div>

                        {/* Review Content */}
                        <p className="text-secondary-700 mb-3 text-sm leading-relaxed flex-1">
                          "{review.content}"
                        </p>

                        {/* Author */}
                        <div className="mt-auto pt-4 border-t border-gray-200">
                          <h4 className="font-semibold text-secondary-900">{review.author}</h4>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                ))}
              </div>
            </div>


          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section ref={heroRef} className="section-padding bg-gradient-to-br from-primary-50 to-white">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="heading-hero text-gray-900 mb-6">
              Amazing <span className="text-visible-orange">Transformations</span>
            </h1>
            <p className="text-section-body">
              Real people, real results. See how our clients have transformed their lives with personalized nutrition guidance
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section id="transformations-stats" ref={statsRef} className="section-padding-sm bg-white">
        <div className="container-max">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-lg mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={statsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl md:text-4xl font-extrabold text-visible-orange mb-1">{stat.value}</div>
                <div className="text-xs text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-8 bg-white border-b">
        <div className="container-max">
          <div className="flex justify-center">
            <div className="flex bg-gray-100 rounded-xl p-2">
              <button
                onClick={() => setActiveTab('journey')}
                className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'journey'
                    ? 'bg-green-500 text-white shadow-md'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                <FiUsers className="w-5 h-5" />
                <span>Journey</span>
              </button>
              <button
                onClick={() => setActiveTab('reviews')}
                className={`px-8 py-3 rounded-lg font-medium transition-all duration-200 flex items-center space-x-2 ${
                  activeTab === 'reviews'
                    ? 'bg-green-500 text-white shadow-md'
                    : 'text-secondary-600 hover:text-secondary-900'
                }`}
              >
                <FiStar className="w-5 h-5" />
                <span>Reviews</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          {activeTab === 'journey' ? renderJourneyTab() : renderReviewsTab()}
        </motion.div>
      </AnimatePresence>

      {/* CTA Section */}
      <section className="section-padding bg-gradient-to-r from-primary-100 to-primary-200 text-neutral-800">
        <div className="container-max">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h2 className="heading-section text-gray-900 mb-4">
              Ready to Write Your Success Story?
            </h2>
            <p className="text-section-body mb-6">
              Join hundreds of clients who have achieved their health goals with personalized guidance
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-accent">
                Start Your Transformation
              </Link>
              <Link to="/services" className="btn-secondary">
                View Our Programs
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Transformations;
